<template>
  <div class="container mx-auto p-6">
    <h1 class="text-3xl font-bold mb-4 text-center">
      Atribuir Controles de Segurança da {{ NomeDaEmpresa }}
    </h1>

    <!-- Mensagem de alerta quando não há empresa selecionada -->
    <div
      v-if="!EmpresaID"
      class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4"
    >
      <p>
        Nenhuma empresa selecionada. Selecione uma empresa para atribuir
        controles.
      </p>
    </div>

    <div id="search-container" class="mb-4 flex items-center space-x-4">
      <input
        type="text"
        v-model="searchQuery"
        class="p-2 border border-gray-300 rounded"
        placeholder="Pesquisar (ID, Nome ou Tipo)..."
      />
      <button
        class="btn-primary px-4 py-2 bg-blue-500 text-white rounded"
        @click="loadControllers"
      >
        Buscar
      </button>
      <button
        v-if="filteredControllers.length > 0 && EmpresaID"
        class="btn-primary px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        @click="atribuirTodosControles"
        :disabled="isLoading"
      >
        {{ isLoading ? 'Atribuindo...' : 'Atribuir Todos' }}
      </button>
    </div>

    <!-- Container principal para as duas tabelas -->
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Tabela de controles disponíveis -->
      <div
        class="flex-1 border border-gray-200 rounded-lg overflow-hidden shadow"
      >
        <div class="bg-gray-100 p-3 flex justify-between items-center">
          <h2 class="text-xl font-bold">Controles Disponíveis</h2>
          <span class="text-gray-600 bg-white px-2 py-1 rounded"
            >{{ filteredControllers.length }} itens</span
          >
        </div>
        <div class="overflow-auto" style="max-height: 500px">
          <table class="min-w-full table-auto border-collapse text-black">
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th class="border px-4 py-2">ID</th>
                <th class="border px-4 py-2">Nome</th>
                <th class="border px-4 py-2">Tipo</th>
                <th class="border px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="control in filteredControllers"
                :key="control._id"
                class="hover:bg-gray-50"
              >
                <td class="border px-4 py-2">{{ control.ControllerId }}</td>
                <td class="border px-4 py-2">{{ control.ControllerName }}</td>
                <td class="border px-4 py-2">{{ control.TypesOfControls }}</td>
                <td class="border px-4 py-2">
                  <button
                    class="bg-green-500 text-white px-3 py-1 rounded mr-2 hover:bg-green-600"
                    @click="atribuirControle(control)"
                    :disabled="!EmpresaID || isLoading"
                  >
                    Atribuir
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabela de controles atribuídos -->
      <div
        class="flex-1 border border-gray-200 rounded-lg overflow-hidden shadow"
      >
        <div class="bg-gray-100 p-3 flex justify-between items-center">
          <h2 class="text-xl font-bold">Controles da {{ NomeDaEmpresa }}</h2>
          <span class="text-gray-600 bg-white px-2 py-1 rounded"
            >{{ empresaControllers.length }} itens</span
          >
        </div>
        <div class="overflow-auto" style="max-height: 500px">
          <table
            v-if="empresaControllers.length > 0"
            class="min-w-full table-auto border-collapse text-black"
          >
            <thead class="sticky top-0 bg-gray-100">
              <tr>
                <th class="border px-4 py-2">ID</th>
                <th class="border px-4 py-2">Nome</th>
                <th class="border px-4 py-2">Tipo</th>
                <th class="border px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="control in empresaControllers"
                :key="control._id"
                class="hover:bg-gray-50"
              >
                <td class="border px-4 py-2">{{ control.ControllerId }}</td>
                <td class="border px-4 py-2">{{ control.ControllerName }}</td>
                <td class="border px-4 py-2">{{ control.TypesOfControls }}</td>
                <td class="border px-4 py-2">
                  <button
                    class="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    @click="removerControle(control)"
                    :disabled="isLoading"
                  >
                    Remover
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="p-4 text-center text-gray-500">
            Nenhum controle atribuído a esta empresa
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getControllers,
  getControllersByEnterpriseId,
  assignController,
} from '@/services/security-controls/api.controllers'
export default {
  props: {
    EmpresaID: String,
    NomeDaEmpresa: String,
  },
  data() {
    return {
      searchQuery: '',
      controllers: [],
      empresaControllers: [],
      isLoading: false,
      error: null,
    }
  },
  computed: {
    filteredControllers() {
      return this.controllers
        .filter(
          (control) =>
            !this.empresaControllers.some(
              (empControl) => empControl._id === control._id
            )
        )
        .filter((control) => {
          const query = this.searchQuery.toLowerCase()
          return (
            control.ControllerId.toString().includes(query) ||
            control.ControllerName.toLowerCase().includes(query) ||
            control.TypesOfControls.toLowerCase().includes(query)
          )
        })
    },
  },
  methods: {
    sortControllers(controllers) {
      return controllers.sort((a, b) => {
        const parseId = (id) => {
          const parts = id.toString().split(/[\.-]/).map(Number)
          return parts
        }

        const aParts = parseId(a.ControllerId)
        const bParts = parseId(b.ControllerId)

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0
          const bVal = bParts[i] || 0
          if (aVal !== bVal) {
            return aVal - bVal
          }
        }

        return 0
      })
    },

    async loadControllers() {
      try {
        this.isLoading = true
        this.error = null

        const [allControllers, empresaControllers] = await Promise.all([
          getControllers(),
          this.EmpresaID
            ? getControllersByEnterpriseId(this.EmpresaID).catch((e) => {
                console.warn(
                  'Empresa não encontrada, iniciando com lista vazia'
                )
                return []
              })
            : Promise.resolve([]),
        ])

        this.controllers = this.sortControllers(allControllers)
        this.empresaControllers = this.sortControllers(empresaControllers)
      } catch (error) {
        console.error('Erro ao carregar controladores:', error)
        this.error = error
        this.$toast.error('Erro ao carregar dados. Por favor, tente novamente.')
      } finally {
        this.isLoading = false
      }
    },

    async atribuirControle(control) {
      if (!this.EmpresaID) return

      try {
        this.isLoading = true
        await assignController(control._id, {
          EmpresaID: this.EmpresaID,
          action: 'add',
        })
        this.empresaControllers = this.sortControllers([
          ...this.empresaControllers,
          control,
        ])
        this.$toast.success('Controle atribuído com sucesso!')
      } catch {
      } finally {
        this.isLoading = false
      }
    },

    async removerControle(control) {
      try {
        this.isLoading = true
        await assignController(control._id, {
          EmpresaID: this.EmpresaID,
          action: 'remove',
        })
        this.empresaControllers = this.empresaControllers.filter(
          (c) => c._id !== control._id
        )
        this.$toast.success('Controle removido com sucesso!')
      } catch {
      } finally {
        this.isLoading = false
      }
    },

    async atribuirTodosControles() {
      try {
        this.isLoading = true

        const atribuicoes = this.filteredControllers.map((control) =>
          assignController(control._id, {
            EmpresaID: this.EmpresaID,
            action: 'add',
          })
        )

        await Promise.all(atribuicoes)

        this.empresaControllers = this.sortControllers([
          ...this.empresaControllers,
          ...this.filteredControllers.map((control) => ({
            ...control,
            ControllerId: Number(control.ControllerId),
          })),
        ])

        this.$toast.success('Todos os controles foram atribuídos com sucesso!')
      } catch {
      } finally {
        this.isLoading = false
      }
    },
  },

  watch: {
    EmpresaID: {
      immediate: true,
      handler() {
        this.loadControllers()
      },
    },
  },
}
</script>

<style scoped>
.btn-primary:hover {
  transform: translateY(-1px);
  transition: transform 0.2s;
}
</style>
